import { useCallback } from "react";
import { useApi } from "../callApi/callApi";
import {
  CreateLeagueResponse,
  GetLeaguesResponse,
  JoinLeagueResponse,
  League,
} from "../../types/league";
import { CreateLeagueRequest } from "../../types/league";
import {
  GetSelectedGamesResponse,
  UpdateSelectedGamesRequest,
  GetPicksResponse,
  UpdatePicksRequest,
  GetAllPicksForWeekResponse,
} from "../../features/cfbd/types/game";

interface shareCodeResponse {
  shareCode: string;
}

/**
 * Custom hook for league operations
 */
export const useLeague = () => {
  const callApi = useApi();

  const createLeague = useCallback(
    async (req: CreateLeagueRequest): Promise<CreateLeagueResponse | Error> => {
      try {
        const response = await callApi(
          `leagues`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(req),
          },
          ""
        );
        if (!response.ok) throw new Error("Failed to create league");
        const responseData: CreateLeagueResponse = await response.json();
        return responseData;
      } catch (err: any) {
        return err instanceof Error ? err : new Error("Unknown error");
      }
    },
    [callApi]
  );

  const getLeagues = useCallback(async (): Promise<
    GetLeaguesResponse | Error
  > => {
    try {
      const response = await callApi(
        `leagues`,
        {
          method: "GET",
        },
        "openid email profile"
      );
      if (!response.ok) throw new Error("Failed to fetch leagues data");
      const leagueData: GetLeaguesResponse = await response.json();
      return leagueData;
    } catch (err: any) {
      return err instanceof Error ? err : new Error("Unknown error");
    }
  }, [callApi]);

  const getLeague = useCallback(
    async (leagueId: string): Promise<League | Error> => {
      try {
        const response = await callApi(
          `leagues/${leagueId}`,
          {
            method: "GET",
          },
          ""
        );
        if (!response.ok) throw new Error("Failed to fetch league data");
        const leagueData: League = await response.json();
        return leagueData;
      } catch (err: any) {
        return err instanceof Error ? err : new Error("Unknown error");
      }
    },
    [callApi]
  );

  const getShareCode = useCallback(
    async (leagueId: string): Promise<string | Error> => {
      try {
        const response = await callApi(
          `leagues/${leagueId}/sharecode`,
          {
            method: "GET",
          },
          ""
        );
        if (!response.ok) throw new Error("Failed to fetch share code");
        const code: shareCodeResponse = await response.json();
        return code.shareCode;
      } catch (err: any) {
        return err instanceof Error ? err : new Error("Unknown error");
      }
    },
    [callApi]
  );

  const joinLeague = useCallback(
    async (shareCode: string): Promise<JoinLeagueResponse | Error> => {
      try {
        const jsonPayload = {
          shareCode,
        };

        const response = await callApi(
          `leagues/join`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(jsonPayload),
          },
          ""
        );
        if (!response.ok) throw new Error("Failed to join league");
        const code: JoinLeagueResponse = await response.json();
        return code;
      } catch (err: any) {
        return err instanceof Error ? err : new Error("Unknown error");
      }
    },
    [callApi]
  );

  const getSelectedGames = useCallback(
    async (
      leagueId: string,
      year: number,
      week: number
    ): Promise<GetSelectedGamesResponse | Error> => {
      try {
        const gamesUrl =
          year > 0 && week > 0
            ? `leagues/${leagueId}/selectedgames?year=${year}&week=${week}`
            : `leagues/${leagueId}/selectedgames`;

        const response = await callApi(
          gamesUrl,
          {
            method: "GET",
          },
          ""
        );
        if (!response.ok) throw new Error("Failed to get selected games");
        const selectedGamesData: GetSelectedGamesResponse =
          await response.json();
        return selectedGamesData;
      } catch (err: any) {
        return err instanceof Error ? err : new Error("Unknown error");
      }
    },
    [callApi]
  );

  const updateSelectedGames = useCallback(
    async (
      leagueId: string,
      year: number,
      week: number,
      req: UpdateSelectedGamesRequest
    ): Promise<boolean | Error> => {
      try {
        const response = await callApi(
          `leagues/${leagueId}/selectedgames?year=${year}&week=${week}`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(req),
          },
          ""
        );
        if (!response.ok) throw new Error("Failed to update selected games");
        return true;
      } catch (err: any) {
        return err instanceof Error ? err : new Error("Unknown error");
      }
    },
    [callApi]
  );

  const getPicks = useCallback(
    async (leagueId: string): Promise<GetPicksResponse | Error> => {
      try {
        const response = await callApi(
          `leagues/${leagueId}/pickedgames`,
          {
            method: "GET",
          },
          ""
        );
        if (!response.ok) throw new Error("Failed to get picks");
        const picksData: GetPicksResponse = await response.json();
        return picksData;
      } catch (err: any) {
        return err instanceof Error ? err : new Error("Unknown error");
      }
    },
    [callApi]
  );

  const getAllPicksForWeek = useCallback(
    async (
      leagueId: string,
      year: number,
      week: number
    ): Promise<GetAllPicksForWeekResponse | Error> => {
      try {
        const picksUrl =
          year > 0 && week > 0
            ? `leagues/${leagueId}/visiblegames?year=${year}&week=${week}`
            : `leagues/${leagueId}/visiblegames`;
        const response = await callApi(
          picksUrl,
          {
            method: "GET",
          },
          ""
        );
        if (!response.ok) throw new Error("Failed to get all picks for week");
        const picksData: GetAllPicksForWeekResponse = await response.json();
        return picksData;
      } catch (err: any) {
        return err instanceof Error ? err : new Error("Unknown error");
      }
    },
    [callApi]
  );

  const updatePicks = useCallback(
    async (
      leagueId: string,
      req: UpdatePicksRequest
    ): Promise<boolean | Error> => {
      try {
        const response = await callApi(
          `leagues/${leagueId}/pickedgames`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(req),
          },
          ""
        );
        if (!response.ok) throw new Error("Failed to update picks");
        return true;
      } catch (err: any) {
        return err instanceof Error ? err : new Error("Unknown error");
      }
    },
    [callApi]
  );

  const updateGameLine = useCallback(
    async (
      leagueId: string,
      gameId: string,
      spread: number,
      year?: number,
      week?: number
    ): Promise<boolean | Error> => {
      try {
        const url = `leagues/${encodeURIComponent(
          leagueId
        )}/games/${encodeURIComponent(gameId)}/lines?${
          year ? `year=${year}` : ""
        }${year && week ? "&" : ""}${week ? `week=${week}` : ""}`;

        const response = await callApi(
          url,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ spread }),
          },
          ""
        );
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`${response.status} ${errorText}`);
        }
        return true;
      } catch (err: any) {
        return err instanceof Error ? err : new Error("Unknown error");
      }
    },
    [callApi]
  );

  const updateGameFlavor = useCallback(
    async (
      leagueId: string,
      gameId: string,
      text: string,
      year?: number,
      week?: number
    ): Promise<boolean | Error> => {
      try {
        const url = `leagues/${encodeURIComponent(
          leagueId
        )}/games/${encodeURIComponent(gameId)}/flavors?${
          year ? `year=${year}` : ""
        }${year && week ? "&" : ""}${week ? `week=${week}` : ""}`;

        const response = await callApi(
          url,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ text }),
          },
          ""
        );
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`${response.status} ${errorText}`);
        }
        return true;
      } catch (err: any) {
        return err instanceof Error ? err : new Error("Unknown error");
      }
    },
    [callApi]
  );

  return {
    createLeague,
    getLeagues,
    getLeague,
    getShareCode,
    joinLeague,
    getSelectedGames,
    updateSelectedGames,
    getPicks,
    getAllPicksForWeek,
    updatePicks,
    updateGameLine,
    updateGameFlavor,
  };
};

export default useLeague;
